import {getTestProps} from '@benchling/js-test-utils';
import {Flex, Paper, Text, Transition, createStyles} from '@mantine/core';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import CHAT_INPUT_TIPS from './ChatInputTipsData';

const SHOWN_TIPS_STORAGE_KEY = 'benchling-chat-input-shown-tips';
const TIP_CYCLE_INTERVAL_MS = 120000;

function getShownCounts(): Record<string, number> {
  try {
    const raw = localStorage.getItem(SHOWN_TIPS_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    return JSON.parse(raw) as Record<string, number>;
  } catch (err) {
    if (err instanceof SyntaxError) {
      return {};
    }
    throw err;
  }
}

function recordShown(tipId: string): void {
  try {
    const counts = getShownCounts();
    counts[tipId] = (counts[tipId] ?? 0) + 1;
    localStorage.setItem(SHOWN_TIPS_STORAGE_KEY, JSON.stringify(counts));
  } catch (err) {
    if (err instanceof DOMException) {
      return;
    }
    throw err;
  }
}

/** Pick the tip that has been shown the fewest times. Ties are broken randomly. */
function pickLeastShownTip(tips: ReadonlyArray<{id: string}>, counts: Record<string, number>): number {
  let minCount = Infinity;
  for (const tip of tips) {
    const c = counts[tip.id] ?? 0;
    if (c < minCount) {
      minCount = c;
    }
  }
  const candidates: Array<number> = [];
  for (let i = 0; i < tips.length; i++) {
    if ((counts[tips[i].id] ?? 0) === minCount) {
      candidates.push(i);
    }
  }
  return candidates[Math.floor(Math.random() * candidates.length)];
}

const useStyles = createStyles((theme) => ({
  icon: {
    color: theme.colors.blue[6],
    fontSize: theme.fontSizes.md,
    flexShrink: 0,
    marginTop: 2,
  },
  title: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 700,
    lineHeight: 1.5,
    color: theme.colors.dark[7],
  },
  text: {
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.5,
    color: theme.colors.dark[7],
  },
}));

function ChatInputTipsInner(): JSX.Element {
  const {classes} = useStyles();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [currentIndex, setCurrentIndex] = useState(() => {
    const counts = getShownCounts();
    const index = pickLeastShownTip(CHAT_INPUT_TIPS, counts);
    recordShown(CHAT_INPUT_TIPS[index].id);
    return index;
  });
  const [mounted, setMounted] = useState(true);

  const advanceTip = useCallback(() => {
    setMounted(false);
    timeoutRef.current = setTimeout(() => {
      const counts = getShownCounts();
      const nextIndex = pickLeastShownTip(CHAT_INPUT_TIPS, counts);
      recordShown(CHAT_INPUT_TIPS[nextIndex].id);
      setCurrentIndex(nextIndex);
      setMounted(true);
    }, 200);
  }, []);

  useEffect(() => {
    const interval = setInterval(advanceTip, TIP_CYCLE_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      clearTimeout(timeoutRef.current);
    };
  }, [advanceTip]);

  const tip = CHAT_INPUT_TIPS[currentIndex];

  return (
    <Transition mounted={mounted} transition="fade" duration={200}>
      {(transitionStyles) => (
        <Paper
          withBorder={true}
          radius="lg"
          p="md"
          mt="sm"
          style={transitionStyles}
          sx={(theme) => ({
            borderColor: theme.colors.blue[1],
            backgroundColor: theme.colors.blue[0],
          })}
          {...getTestProps({component: 'ChatInputTips', element: 'root'})}
        >
          <Flex align="flex-start" gap="sm">
            <BenchlingIcon icon={tip.icon} className={classes.icon} />
            <div>
              <Text className={classes.title}>Tip: {tip.title}</Text>
              <Text className={classes.text}>{tip.text}</Text>
            </div>
          </Flex>
        </Paper>
      )}
    </Transition>
  );
}

export default function ChatInputTips(): JSX.Element | null {
  const {llmChatTips} = useRolloutFlags();
  if (llmChatTips !== true) {
    return null;
  }
  return <ChatInputTipsInner />;
}
